import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowUpRight, Phone, MessageSquare, Flame, Target, TrendingUp, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from "recharts";

const STAGES = [
  { key: "scraped", label: "Scraped" },
  { key: "contacted", label: "Contacted" },
  { key: "answered", label: "Answered" },
  { key: "qualified", label: "Qualified" },
  { key: "booked", label: "Booked" },
  { key: "demo", label: "Demo" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [queue, setQueue] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, a, q] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/activities?limit=20"),
          api.get("/sequences/queue"),
        ]);
        setStats(s.data);
        setActivities(a.data);
        setQueue(q.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const chartData = stats
    ? STAGES.map((s) => ({ name: s.label, count: stats.pipeline[s.key] || 0 }))
    : [];

  return (
    <div className="p-8 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6 mb-8">
        <div>
          <div className="overline mb-2">/ command center</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">Dashboard</h1>
        </div>
        <button
          onClick={() => navigate("/scraper")}
          data-testid="cta-scrape"
          className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors"
        >
          Find New Leads <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          testid="kpi-hot"
          icon={<Flame className="w-4 h-4" />}
          label="Hot Leads"
          value={stats?.hot_leads ?? "—"}
          action="View Pipeline"
          onClick={() => navigate("/pipeline")}
          accent="#EF4444"
        />
        <KpiCard
          testid="kpi-total"
          icon={<Target className="w-4 h-4" />}
          label="Total Leads"
          value={stats?.total_leads ?? "—"}
          action="View Leads"
          onClick={() => navigate("/leads")}
          accent="#002FA7"
        />
        <KpiCard
          testid="kpi-calls"
          icon={<Phone className="w-4 h-4" />}
          label="AI Calls Made"
          value={stats?.calls_made ?? "—"}
          action="View Activity"
          accent="#0A0A0A"
        />
        <KpiCard
          testid="kpi-sms"
          icon={<MessageSquare className="w-4 h-4" />}
          label="SMS Sent"
          value={stats?.sms_sent ?? "—"}
          action="View Activity"
          accent="#10B981"
        />
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity feed */}
        <div className="lg:col-span-2 border border-zinc-200 bg-white">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="overline">/ ai activity feed</div>
              <h2 className="font-heading text-lg font-bold mt-1">Live SDR Stream</h2>
            </div>
            <TrendingUp className="w-4 h-4 text-zinc-400" />
          </div>
          <div className="max-h-[440px] overflow-y-auto" data-testid="activity-feed">
            {activities.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-zinc-500">
                No activity yet. Start by scraping leads.
              </div>
            ) : (
              activities.map((a) => (
                <div key={a.id} className="px-5 py-3 border-b border-zinc-100 last:border-0 flex gap-3">
                  <ActivityIcon kind={a.kind} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.summary}</div>
                    {a.body && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{a.body}</div>}
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-400 mt-1">
                      {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pipeline chart */}
        <div className="border border-zinc-200 bg-white">
          <div className="px-5 py-4 border-b border-zinc-200">
            <div className="overline">/ pipeline</div>
            <h2 className="font-heading text-lg font-bold mt-1">By Stage</h2>
            <div className="text-xs text-zinc-500 mt-1 font-mono">
              Conversion: {stats?.conversion_rate ?? 0}%
            </div>
          </div>
          <div className="p-4 h-[260px]" data-testid="pipeline-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid stroke="#E4E4E7" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#A1A1AA" />
                <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#A1A1AA" allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(0,47,167,0.06)" }} contentStyle={{ borderRadius: 0, border: "1px solid #E4E4E7", fontSize: 12 }} />
                <Bar dataKey="count" fill="#002FA7" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Sequence queue */}
          <div className="border-t border-zinc-200">
            <div className="px-5 py-3 flex items-center justify-between">
              <div>
                <div className="overline">/ ai sequence queue</div>
                <div className="font-mono text-xs text-zinc-500 mt-1">{queue.length} scheduled</div>
              </div>
              <Clock className="w-4 h-4 text-zinc-400" />
            </div>
            <div className="max-h-[180px] overflow-y-auto" data-testid="queue-widget">
              {queue.length === 0 ? (
                <div className="px-5 py-6 text-center text-xs text-zinc-500">
                  No queued actions. Enable sequences in Settings.
                </div>
              ) : (
                queue.slice(0, 6).map((q) => (
                  <Link
                    key={q.id}
                    to={`/leads/${q.id}`}
                    data-testid={`queue-item-${q.id}`}
                    className="px-5 py-2 border-t border-zinc-100 hover:bg-zinc-50 flex items-center justify-between no-underline text-inherit"
                  >
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{q.name}</div>
                      <div className="font-mono text-[10px] text-zinc-500 uppercase">
                        {q.next_action_kind} · {new Date(q.next_action_at).toLocaleString()}
                      </div>
                    </div>
                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-amber-100 text-amber-700">
                      {q.next_action_kind}
                    </span>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, action, onClick, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-zinc-200 bg-white p-5 hover:shadow-sm transition-shadow cursor-pointer group"
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="overline">{label}</div>
        <div style={{ color: accent }}>{icon}</div>
      </div>
      <div className="font-heading text-4xl font-black tracking-tight mb-3">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-600 group-hover:text-[#002FA7] transition-colors flex items-center gap-1">
        {action} <ArrowUpRight className="w-3 h-3" />
      </div>
    </div>
  );
}

function ActivityIcon({ kind }) {
  const map = {
    call: { icon: <Phone className="w-3.5 h-3.5" />, bg: "#0A0A0A", color: "white" },
    sms: { icon: <MessageSquare className="w-3.5 h-3.5" />, bg: "#10B981", color: "white" },
    ai_score: { icon: <Flame className="w-3.5 h-3.5" />, bg: "#F59E0B", color: "white" },
    scrape: { icon: <Target className="w-3.5 h-3.5" />, bg: "#002FA7", color: "white" },
    stage_change: { icon: <TrendingUp className="w-3.5 h-3.5" />, bg: "#52525B", color: "white" },
  };
  const conf = map[kind] || { icon: <TrendingUp className="w-3.5 h-3.5" />, bg: "#A1A1AA", color: "white" };
  return (
    <div className="w-7 h-7 flex items-center justify-center shrink-0" style={{ background: conf.bg, color: conf.color }}>
      {conf.icon}
    </div>
  );
}
